import * as yup from "yup";
import { enums } from "../constants/enum";

const commonSchema = {
  brand: yup.string().when("category", {
    is: (val) =>
      val === enums.ITEM_CATEGORIES.LENSE ||
      val === enums.ITEM_CATEGORIES.ACCESSORIES,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Brand is required"),
  }),
  itemName: yup.string().required("Item Name is required"),
  category: yup
    .string()
    .oneOf(Object.values(enums.ITEM_CATEGORIES), "Invalid category")
    .required("Category is required"),
  purchasePrice: yup
    .number()
    .typeError("Purchase Price must be a number")
    .positive("Purchase Price must be greater than 0")
    .required("Purchase Price is required"),
  sellingPrice: yup
    .number()
    .typeError("Selling Price must be a number")
    .positive("Selling Price must be greater than 0")
    .required("Selling Price is required"),
  warrantyPeriodMonths: yup
    .number()
    .typeError("Warranty Period must be a number")
    .min(0, "Warranty Period must be at least 0 months")
    .required("Warranty Period is required"),
  stock: yup.object().shape({
    stockStatus: yup
      .string()
      .oneOf(Object.values(enums.STOCK_STATUS), "Invalid stock status")
      .required("Stock Status is required"),
    storeRoomStock: yup.number().when("stockStatus", {
      is: enums.STOCK_STATUS.READY,
      then: (schema) =>
        schema
          .typeError("Storeroom Stock must be a number")
          .min(0, "Storeroom Stock must be at least 0")
          .required("Storeroom Stock is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    displayStock: yup.number().when("stockStatus", {
      is: enums.STOCK_STATUS.READY,
      then: (schema) =>
        schema
          .typeError("Showroom Stock must be a number")
          .min(0, "Showroom Stock must be at least 0")
          .required("Showroom Stock is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
};

const numberOrPlano = (min, max, label) =>
  yup
    .mixed()
    .nullable()
    .test("is-valid-format", `${label} must be a number or 'PLANO'`, (value) => {
      if (value === null || value === undefined || value === "") return true;
      if (value === "PLANO") return true;
      return !isNaN(parseFloat(value)) && isFinite(value);
    })
    .test("min", `${label} must be at least ${min}`, (value) => {
      if (value === "PLANO" || value == null || value === "") return true;
      return parseFloat(value) >= min;
    })
    .test("max", `${label} must not exceed ${max}`, (value) => {
      if (value === "PLANO" || value == null || value === "") return true;
      return parseFloat(value) <= max;
    })
    .test(
      "step",
      `${label} must be in steps of 0.25 (e.g., -1.25, -1.50)`,
      (value) => {
        if (value === "PLANO" || value == null || value === "") return true;
        return Math.round(parseFloat(value) * 100) % 25 === 0;
      },
    );

const lenseAttributesSchema = yup.object().shape({
  lense_type: yup
    .string()
    .oneOf(Object.values(enums.LENSE_TYPES), "Invalid lense type")
    .required("Lense type is required"),
  seg: yup.string().nullable().default(null),
  power_range: yup
    .object()
    .shape({
      sph: numberOrPlano(-25, 25, "SPH"),
      cyl: numberOrPlano(-10, 0, "CYL"),
      add: yup
        .number()
        .min(0, "ADD must be at least 0")
        .max(5, "ADD must be at most 5")
        .typeError("ADD must be a number")
        .notRequired(),
    })
    .test(
      "at-least-one",
      "At least one of SPH or CYL is required",
      function (value) {
        const { sph, cyl } = value || {};
        const hasValue = (val) =>
          val !== "" &&
          val !== null &&
          val !== undefined &&
          val !== 0 &&
          val !== "0";
        if (hasValue(sph) || hasValue(cyl)) {
          return true;
        }
        return this.createError({
          path: `${this.path}.sph`,
          message: "At least one of SPH or CYL is required",
        });
      },
    )
    .required("Power range details are required"),
  coating: yup
    .string()
    .trim("Coating must not contain leading or trailing spaces")
    .oneOf(Object.values(enums.LENSE_COATINGS), "Invalid coating")
    .required("Coating is required"),
  transition: yup.mixed().when("coating", {
    is: enums.LENSE_COATINGS.PHOTO_GREY,
    then: () =>
      yup
        .number()
        .typeError("Transition level must be a number")
        .min(1, "Transition level must be at least 1")
        .max(4, "Transition level must be at most 4")
        .integer("Transition level must be an integer")
        .required("Transition level is required for PHOTO_GREY coating"),
    otherwise: (schema) =>
      schema
        .nullable()
        .oneOf(
          [null],
          "Transition level must be null for coatings other than PHOTO_GREY",
        ),
  }),
  manufacture_country: yup
    .string()
    .trim("Manufacture country must not contain leading or trailing spaces")
    .notRequired(),
  thickness: yup
    .number()
    .min(0, "Thickness must be at least 0")
    .typeError("Thickness must be a number")
    .notRequired(),
  index: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .typeError("Index must be a number")
    .notRequired(),
  free_form_design: yup
    .string()
    .oneOf(Object.values(enums.FREE_FORM_DESIGNS), "Invalid free form design")
    .notRequired(),
  tint: yup.string().notRequired(),
});

const frameAttributesSchema = yup.object().shape({
  size: yup.object().shape({
    lens_diagonal: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .typeError("Lens Diagonal must be a number")
      .positive("Lens Diagonal must be positive")
      .notRequired(),
    bridge: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .typeError("Bridge must be a number")
      .positive("Bridge must be positive")
      .notRequired(),
    arm_length: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .typeError("Arm Length must be a number")
      .positive("Arm Length must be positive")
      .notRequired(),
  }),
  material: yup.string().required("Material is required"),
  color: yup.string().required("Color is required"),
  model_number: yup.string().notRequired(),
});

const sunglassAttributesSchema = yup.object().shape({
  frame_material: yup.string().required("Frame Material is required"),
  frame_color: yup.string().required("Frame Color is required"),
  shape: yup.string().required("Shape is required"),
  lens_type: yup.string().required("Lens Type is required"),
  gender: yup
    .string()
    .oneOf(Object.values(enums.GENDER), "Invalid gender")
    .required("Gender is required"),
});

const accessoriesAttributesSchema = yup.object().shape({
  type: yup.string().notRequired(),
  material: yup.string().notRequired(),
  color: yup.string().required("Color is required"),
});

export const itemSchema = yup.object().shape({
  ...commonSchema,
  attributes: yup.mixed().when("category", {
    is: enums.ITEM_CATEGORIES.LENSE,
    then: () => lenseAttributesSchema,
    otherwise: (schema) =>
      schema.when("category", {
        is: enums.ITEM_CATEGORIES.FRAME,
        then: () => frameAttributesSchema,
        otherwise: (schema) =>
          schema.when("category", {
            is: enums.ITEM_CATEGORIES.SUNGLASS,
            then: () => sunglassAttributesSchema,
            otherwise: (schema) =>
              schema.when("category", {
                is: enums.ITEM_CATEGORIES.ACCESSORIES,
                then: () => accessoriesAttributesSchema,
                otherwise: (schema) => schema.notRequired(),
              }),
          }),
      }),
  }),
});

const commonItemUpdateSchema = {
  brand: yup.string().when("category", {
    is: (val) =>
      val === enums.ITEM_CATEGORIES.LENSE ||
      val === enums.ITEM_CATEGORIES.ACCESSORIES,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Brand is required"),
  }),
  itemName: yup.string().required("Item Name is required"),
  category: yup
    .string()
    .oneOf(Object.values(enums.ITEM_CATEGORIES), "Invalid category")
    .required("Category is required"),
  purchasePrice: yup
    .number()
    .typeError("Purchase Price must be a number")
    .positive("Purchase Price must be greater than 0")
    .required("Purchase Price is required"),
  sellingPrice: yup
    .number()
    .typeError("Selling Price must be a number")
    .positive("Selling Price must be greater than 0")
    .required("Selling Price is required"),
  warrantyPeriodMonths: yup
    .number()
    .typeError("Warranty Period must be a number")
    .min(0, "Warranty Period must be at least 0 months")
    .required("Warranty Period is required"),
};

export const itemUpdateSchema = yup.object().shape({
  ...commonItemUpdateSchema,
  attributes: yup.mixed().when("category", {
    is: enums.ITEM_CATEGORIES.LENSE,
    then: () => lenseAttributesSchema,
    otherwise: (schema) =>
      schema.when("category", {
        is: enums.ITEM_CATEGORIES.FRAME,
        then: () => frameAttributesSchema,
        otherwise: (schema) =>
          schema.when("category", {
            is: enums.ITEM_CATEGORIES.SUNGLASS,
            then: () => sunglassAttributesSchema,
            otherwise: (schema) =>
              schema.when("category", {
                is: enums.ITEM_CATEGORIES.ACCESSORIES,
                then: () => accessoriesAttributesSchema,
                otherwise: (schema) => schema.notRequired(),
              }),
          }),
      }),
  }),
});
